import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Save, Loader2, Edit2, X, FileText, Plus, Upload, Eye, Download,
  Calendar, Euro, Gauge, Building, Phone, Mail, Check
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { DatePickerFr } from "../../ui/date-picker-fr";
import {
  ACQUISITION_TYPE_OPTIONS,
  ACQUISITION_TYPE_LABELS,
  CONTRACT_STATUS_LABELS,
  formatCurrency,
  formatMileage,
  formatDate,
  getDaysUntilLabel,
} from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContractTab({ vehicle, onUpdate }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeContract, setActiveContract] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [form, setForm] = useState({});
  
  const token = localStorage.getItem("token");

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicle.id}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const contractsList = response.data.contracts || [];
      setContracts(contractsList);
      
      // Find active contract
      const active = contractsList.find(c => c.status === "active");
      setActiveContract(active || null);
      
      if (active) {
        initForm(active);
      }
    } catch (err) {
      console.error("Error fetching contracts:", err);
      toast.error("Erreur lors du chargement des contrats");
    } finally {
      setLoading(false);
    }
  }, [vehicle.id, token]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const initForm = (contract) => {
    setForm({
      contract_type: contract?.contract_type || "lld",
      contract_number: contract?.contract_number || "",
      lessor_name: contract?.lessor_name || "",
      lessor_contact: contract?.lessor_contact || "",
      lessor_phone: contract?.lessor_phone || "",
      signature_date: contract?.signature_date || "",
      start_date: contract?.start_date || "",
      end_date: contract?.end_date || "",
      duration_months: contract?.duration_months || "",
      monthly_rent_ht: contract?.monthly_rent_ht || "",
      monthly_rent_ttc: contract?.monthly_rent_ttc || "",
      deposit: contract?.deposit || "",
      buyout_value: contract?.buyout_value || "",
      total_allowed_mileage: contract?.total_allowed_mileage || "",
      annual_allowed_mileage: contract?.annual_allowed_mileage || "",
      excess_mileage_rate: contract?.excess_mileage_rate || "",
      below_mileage_refund_rate: contract?.below_mileage_refund_rate || "",
      return_franchise: contract?.return_franchise || "",
      options: contract?.options || {
        maintenance: false,
        insurance: false,
        tires: false,
        assistance: false,
        replacement_vehicle: false
      }
    });
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (option, checked) => {
    setForm(prev => ({
      ...prev,
      options: { ...prev.options, [option]: checked }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      
      // Convert numeric fields
      const numericFields = [
        "duration_months", "monthly_rent_ht", "monthly_rent_ttc", "deposit",
        "buyout_value", "total_allowed_mileage", "annual_allowed_mileage",
        "excess_mileage_rate", "below_mileage_refund_rate", "return_franchise"
      ];
      
      numericFields.forEach(field => {
        if (payload[field] !== "" && payload[field] !== null) {
          payload[field] = parseFloat(payload[field]);
        } else {
          delete payload[field];
        }
      });
      
      // Clean empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") delete payload[key];
      });
      
      if (activeContract) {
        // Update existing contract
        await axios.put(
          `${API}/fleet/vehicles/${vehicle.id}/contracts/${activeContract.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Contrat mis à jour");
      } else {
        // Create new contract
        await axios.post(
          `${API}/fleet/vehicles/${vehicle.id}/contracts`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Contrat créé");
      }
      
      setEditing(false);
      fetchContracts();
      onUpdate?.();
    } catch (err) {
      console.error("Error saving contract:", err);
      toast.error("Erreur", {
        description: err.response?.data?.detail || "Impossible de sauvegarder"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeContract) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(
        `${API}/fleet/vehicles/${vehicle.id}/contracts/${activeContract.id}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      toast.success("Document uploadé");
      fetchContracts();
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCancel = () => {
    initForm(activeContract);
    setEditing(false);
  };

  const InfoRow = ({ label, value, highlight }) => (
    <div className="flex justify-between py-2 border-b last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${highlight ? "text-indigo-600" : "text-slate-900"}`}>
        {value || "-"}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  // No contract - show create form
  if (!activeContract && !editing) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun contrat actif</h3>
          <p className="text-slate-500 mb-4">Ajoutez les informations du contrat de leasing.</p>
          <Button onClick={() => { initForm(null); setEditing(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Créer un contrat
          </Button>
        </CardContent>
      </Card>
    );
  }

  // View Mode
  if (!editing && activeContract) {
    const options = activeContract.options || {};
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800">
              {CONTRACT_STATUS_LABELS[activeContract.status]}
            </Badge>
            {activeContract.days_until_end !== null && (
              <span className={`text-sm font-medium ${
                activeContract.days_until_end <= 30 ? "text-red-600" :
                activeContract.days_until_end <= 90 ? "text-orange-600" : "text-slate-600"
              }`}>
                {getDaysUntilLabel(activeContract.days_until_end)}
              </span>
            )}
          </div>
          <Button onClick={() => setEditing(true)} data-testid="edit-contract">
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Identification */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Identification du contrat
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Type" value={ACQUISITION_TYPE_LABELS[activeContract.contract_type]} />
              <InfoRow label="N° de contrat" value={activeContract.contract_number} />
              <InfoRow label="Date de signature" value={formatDate(activeContract.signature_date)} />
              <InfoRow label="Date de début" value={formatDate(activeContract.start_date)} />
              <InfoRow label="Date de fin" value={formatDate(activeContract.end_date)} highlight />
              <InfoRow label="Durée" value={activeContract.duration_months ? `${activeContract.duration_months} mois` : null} />
            </CardContent>
          </Card>
          
          {/* Loueur */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="w-4 h-4 text-indigo-600" />
                Loueur / Bailleur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Nom" value={activeContract.lessor_name} />
              <InfoRow label="Contact" value={activeContract.lessor_contact} />
              <InfoRow label="Téléphone" value={activeContract.lessor_phone} />
            </CardContent>
          </Card>
          
          {/* Financier */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Euro className="w-4 h-4 text-indigo-600" />
                Conditions financières
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Loyer mensuel HT" value={formatCurrency(activeContract.monthly_rent_ht)} />
              <InfoRow label="Loyer mensuel TTC" value={formatCurrency(activeContract.monthly_rent_ttc)} highlight />
              <InfoRow label="Dépôt de garantie" value={formatCurrency(activeContract.deposit)} />
              <InfoRow label="Valeur de rachat" value={formatCurrency(activeContract.buyout_value)} />
              <InfoRow label="Franchise restitution" value={formatCurrency(activeContract.return_franchise)} />
            </CardContent>
          </Card>
          
          {/* Kilométrage */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Gauge className="w-4 h-4 text-indigo-600" />
                Kilométrage contractuel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Km total autorisé" value={formatMileage(activeContract.total_allowed_mileage)} highlight />
              <InfoRow label="Km autorisé / an" value={formatMileage(activeContract.annual_allowed_mileage)} />
              <InfoRow label="Tarif km dépassé" value={activeContract.excess_mileage_rate ? `${activeContract.excess_mileage_rate} €/km` : null} />
              <InfoRow label="Remb. km non parcourus" value={activeContract.below_mileage_refund_rate ? `${activeContract.below_mileage_refund_rate} €/km` : null} />
            </CardContent>
          </Card>
          
          {/* Options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Options incluses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "maintenance", label: "Entretien" },
                  { key: "insurance", label: "Assurance" },
                  { key: "tires", label: "Pneus" },
                  { key: "assistance", label: "Assistance" },
                  { key: "replacement_vehicle", label: "Véh. remplacement" },
                ].map(opt => (
                  <div key={opt.key} className="flex items-center gap-2">
                    {options[opt.key] ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={options[opt.key] ? "text-slate-900" : "text-slate-400"}>
                      {opt.label}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Document PDF */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Document du contrat
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeContract.contract_file_id ? (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">{activeContract.contract_filename || "Contrat.pdf"}</p>
                      <p className="text-xs text-slate-500">PDF</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`${API}/fleet/files/${activeContract.contract_file_id}/view`, "_blank")}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-3">Aucun document uploadé</p>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                    />
                    <Button variant="outline" size="sm" disabled={uploadingFile} asChild>
                      <span>
                        {uploadingFile ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Uploader le contrat
                      </span>
                    </Button>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Edit Mode
  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleCancel} disabled={saving}>
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={saving} data-testid="save-contract">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Identification du contrat</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contrat</Label>
                <Select value={form.contract_type} onValueChange={(v) => handleChange("contract_type", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACQUISITION_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>N° de contrat</Label>
                <Input
                  value={form.contract_number}
                  onChange={(e) => handleChange("contract_number", e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Date de signature</Label>
              <DatePickerFr
                value={form.signature_date}
                onChange={(v) => handleChange("signature_date", v)}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <DatePickerFr
                  value={form.start_date}
                  onChange={(v) => handleChange("start_date", v)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin *</Label>
                <DatePickerFr
                  value={form.end_date}
                  onChange={(v) => handleChange("end_date", v)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Durée (mois)</Label>
              <Input
                type="number"
                min="1"
                value={form.duration_months}
                onChange={(e) => handleChange("duration_months", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Loueur */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Loueur / Bailleur</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du loueur</Label>
              <Input
                value={form.lessor_name}
                onChange={(e) => handleChange("lessor_name", e.target.value)}
                placeholder="Ex: ALD Automotive"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact email</Label>
              <Input
                value={form.lessor_contact}
                onChange={(e) => handleChange("lessor_contact", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={form.lessor_phone}
                onChange={(e) => handleChange("lessor_phone", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Financier */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Conditions financières</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loyer mensuel HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthly_rent_ht}
                  onChange={(e) => handleChange("monthly_rent_ht", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Loyer mensuel TTC (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthly_rent_ttc}
                  onChange={(e) => handleChange("monthly_rent_ttc", e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dépôt de garantie (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.deposit}
                  onChange={(e) => handleChange("deposit", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Valeur de rachat (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.buyout_value}
                  onChange={(e) => handleChange("buyout_value", e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Franchise restitution (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.return_franchise}
                onChange={(e) => handleChange("return_franchise", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Kilométrage */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Kilométrage contractuel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Km total autorisé</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.total_allowed_mileage}
                  onChange={(e) => handleChange("total_allowed_mileage", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Km autorisé / an</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.annual_allowed_mileage}
                  onChange={(e) => handleChange("annual_allowed_mileage", e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tarif km dépassé (€/km)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.excess_mileage_rate}
                  onChange={(e) => handleChange("excess_mileage_rate", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Remb. km non parcourus (€/km)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.below_mileage_refund_rate}
                  onChange={(e) => handleChange("below_mileage_refund_rate", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Options */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Options incluses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { key: "maintenance", label: "Entretien" },
                { key: "insurance", label: "Assurance" },
                { key: "tires", label: "Pneus" },
                { key: "assistance", label: "Assistance" },
                { key: "replacement_vehicle", label: "Véhicule de remplacement" },
              ].map(opt => (
                <div key={opt.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={opt.key}
                    checked={form.options?.[opt.key] || false}
                    onCheckedChange={(checked) => handleOptionChange(opt.key, checked)}
                  />
                  <label htmlFor={opt.key} className="text-sm cursor-pointer">
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
